/* Spejder Sport PDP — Select-size + Notify-me overlays (card FYGkhHbf),
 * the Størrelsesguide overlay (card aP4qhkPI), the Shipping + Returns
 * content drawers (card wmTwAuGg), the Pricematch content drawer
 * (card ZoQGnp39, B1), the "Om produktet" bundle-item detail
 * drawer (card 1NIULYM3, B2, wired to the Bundle 1 chevron stub from card
 * wSOGNeSd), and the Click & Collect store-directory drawer (card
 * HsOPbWeH, Unit 1) — all reuse the exact same openOverlay()/host
 * convention below.
 *
 * Select-size/Notify-me/Size-guide are rendered ONCE per PDP and reused by
 * every trigger (whole-product-sold-out ATC / sold-out size pills open
 * Notify-me; the "Størrelsesguide" link opens the size-guide drawer; the
 * Select-size overlay is a no-size-selected GUARD — it opens ONLY when the
 * ATC button is clicked with no size explicitly chosen yet. Inline size
 * pills just select in place, no overlay — human-verified correction,
 * retry:3). Shipping/Returns are static content overlays with a single
 * trigger each (the retail-info "Fragt & Levering" row / "Byt & Returner"
 * pill) — same shell, same openOverlay() proxy/focus-return helper.
 *
 * popup-component (src/js/components/popup.js) only wires ONE trigger
 * natively (`this.buttonPopupOpen = this.querySelector('[data-popup-open]')`
 * inside the component, first-match). To reuse its exact focus-trap / ESC /
 * scrim / scroll-lock logic from many external trigger points, each overlay
 * carries a hidden internal `[data-popup-open]` proxy; real triggers update
 * the overlay's content then `.click()` the proxy. Focus-RETURN then needs
 * correcting: popup-component's own click handler sets
 * `window.theme.a11y.lastElement` to the (hidden) proxy — we override it to
 * the real invoking trigger immediately after, synchronously, so ESC/close
 * returns focus to where the user actually was (checklist a11y requirement).
 */
(function () {
  function openOverlay(host, invoker) {
    if (!host) return;
    var proxy = host.querySelector('[data-popup-open]');
    if (!proxy) return;
    proxy.click();
    if (window.theme && window.theme.a11y) {
      window.theme.a11y.lastElement = invoker;
    }
  }

  function closeOverlay(host) {
    if (!host) return;
    var closeBtn = host.querySelector('[data-popup-close]');
    if (closeBtn) closeBtn.click();
  }

  function initPDP(root) {
    /* ---- shared state -------------------------------------------------- */
    var variantsJsonEl = root.querySelector('[data-pdp-variants-json]');
    var variants = [];
    try {
      variants = variantsJsonEl ? JSON.parse(variantsJsonEl.textContent) : [];
    } catch (e) {
      variants = [];
    }
    var rowsRoot = root.querySelector('[data-pdp-variant-rows]');
    var lowStockThreshold = rowsRoot ? Number(rowsRoot.getAttribute('data-low-stock-threshold')) || 5 : 5;
    /* Bundle products (sections/spejder-pdp.liquid, data-bundle-* on the
       section root): the parent variant is untracked, so every per-variant
       stock signal below reads custom.bundle_availability through these —
       sold-out ⇒ every size sold out, no store stock, CTA pinned to notify;
       available ⇒ max_qty (sellable sets) vs the low-stock threshold, store
       count = AND of the components' pickup maps (ccBundleEntry). The
       legacy [data-pdp-bundle][data-bundle-sold-out] hook is kept as a
       fallback for a section render that predates the root attributes. */
    var isBundle = root.hasAttribute('data-bundle');
    var bundleSoldOut = root.hasAttribute('data-bundle-sold-out') || !!root.querySelector('[data-pdp-bundle][data-bundle-sold-out]');
    var bundleMaxQtyAttr = root.getAttribute('data-bundle-max-qty');
    var bundleMaxQty = bundleMaxQtyAttr !== null && bundleMaxQtyAttr !== '' && !isNaN(Number(bundleMaxQtyAttr)) ? Number(bundleMaxQtyAttr) : null;
    var bundleComponentIds = (root.getAttribute('data-bundle-components') || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    /* Bundles where the customer picks a size per product (client card
       UbNVzlvf, 2026-10-16). spejder-pdp-bundle.liquid lists every part in
       [data-bundle-parts] ({s: product slot, id: variant id, q: quantity},
       plus v/o/k for a size the customer can pick) and the units per
       location of every part that can run out in [data-bundle-stock] (the
       Spejder Sport app's custom.bundle_availability.stock; a part missing
       from it never runs out). A bundle ships whole from ONE location, so a
       size is sold out when no single location could then send a whole
       bundle, given the sizes picked for the other products: the app's own
       count (computeSlotAvailability), redone per pick. Picked sizes live in
       bundlePicked (slot -> variant id); the drawer code is further down
       ("Bundle: a size per product"). */
    var bundleSlots = [];
    var bundleStock = null;
    var bundlePicked = {};
    if (isBundle) {
      try {
        var bundlePartsEl = root.querySelector('[data-bundle-parts]');
        var bundleStockEl = root.querySelector('[data-bundle-stock]');
        var bundleSlotsByKey = {};
        (bundlePartsEl ? JSON.parse(bundlePartsEl.textContent) : []).forEach(function (part) {
          var key = String(part.s);
          var slot = bundleSlotsByKey[key];
          if (!slot) {
            slot = bundleSlotsByKey[key] = { key: key, qty: Math.max(1, Number(part.q) || 1), choice: !!part.c, option: part.o || '', choices: [] };
            bundleSlots.push(slot);
          }
          slot.choices.push({ id: String(part.id), value: String(part.v || ''), sku: part.k || '' });
        });
        bundleStock = bundleStockEl ? JSON.parse(bundleStockEl.textContent) : null;
      } catch (err) {
        bundleSlots = [];
        bundleStock = null;
      }
    }
    var bundleChoiceSlots = bundleSlots.filter(function (slot) { return slot.choice; });

    function bundleLimited(variantId) {
      return !!bundleStock && Object.prototype.hasOwnProperty.call(bundleStock, variantId);
    }

    // The sizes a product can still go out in: `trial` when it is that
    // product's, else its pick, else every size it comes in.
    function bundleCandidates(slot, trial) {
      if (trial && trial.slot === slot) return [trial.id];
      if (bundlePicked[slot.key]) return [bundlePicked[slot.key]];
      return slot.choices.map(function (c) { return c.id; });
    }

    // Whole bundles one location can send (Infinity when nothing runs out).
    function bundleSetsAt(locationId, trial) {
      var sets = Infinity;
      bundleSlots.forEach(function (slot) {
        var best = 0;
        bundleCandidates(slot, trial).forEach(function (id) {
          var units = bundleLimited(id) ? Math.floor((Number((bundleStock[id] || {})[locationId]) || 0) / slot.qty) : Infinity;
          if (units > best) best = units;
        });
        if (best < sets) sets = best;
      });
      return sets;
    }

    // Whole bundles over all locations, with the current picks plus `trial`.
    function bundleSetsTotal(trial) {
      var limited = bundleSlots.some(function (slot) {
        return bundleCandidates(slot, trial).every(bundleLimited);
      });
      if (!limited) return Infinity;
      var locations = {};
      Object.keys(bundleStock).forEach(function (id) {
        Object.keys(bundleStock[id] || {}).forEach(function (locationId) { locations[locationId] = true; });
      });
      return Object.keys(locations).reduce(function (sum, locationId) {
        return sum + bundleSetsAt(locationId, trial);
      }, 0);
    }
    var colorSwatchesEl = root.querySelector('[data-pdp-color-option]');
    var colorPosition = colorSwatchesEl ? Number(colorSwatchesEl.getAttribute('data-pdp-color-option')) : null;
    var sizePillsEl = root.querySelector('[data-pdp-size-option]');
    var sizePosition = sizePillsEl ? Number(sizePillsEl.getAttribute('data-pdp-size-option')) : null;
    var optionName = sizePillsEl ? sizePillsEl.getAttribute('data-option-name') : '';

    var productTitleEl = root.querySelector('.spejder-pdp__title');
    var productTitleText = productTitleEl ? productTitleEl.textContent.trim() : document.title;

    var addToCartForm = root.querySelector('[data-product-form]');
    var addToCartBtn = root.querySelector('[data-add-to-cart]');
    var variantIdInput = root.querySelector('.product-variant-id');
    var sizeExplicitlyChosen = false;
    /* Assigned inside the Click & Collect block (needs its config/map);
       called from the variant-pick paths below. var, not function — the
       C&C block only runs when the drawer host exists. */
    var ccApplyRowStatus = null;

    function optionValue(variant, position) {
      if (!variant || !position) return null;
      if (position === 1) return variant.option1;
      if (position === 2) return variant.option2;
      return variant.option3;
    }

    function deriveState(variant) {
      if (!variant) return 'unavailable';
      if (isBundle) {
        if (bundleSoldOut) return 'sold-out';
        if (bundleMaxQty !== null && bundleMaxQty <= lowStockThreshold) return 'low-stock';
        return 'available';
      }
      if (!variant.available) return 'sold-out';
      if (variant.inventory_management === 'shopify' && variant.inventory_quantity <= lowStockThreshold) {
        return 'low-stock';
      }
      return 'available';
    }

    function findVariant(sizeValue, colorValue) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        if (optionValue(v, sizePosition) !== sizeValue) continue;
        if (colorPosition && optionValue(v, colorPosition) !== colorValue) continue;
        return v;
      }
      return null;
    }

    /* Tell the page which variant the shopper just picked. The PDP has no
       <product-info>, so Broadcast's theme.PUB_SUB_EVENTS.variantChange never
       fires here — this event is what src/js/utils/relewise-tracking.js
       listens for to send Relewise a fresh ProductView with the variant SKU
       (Relewise go-live check 2026-09-15: only the landing variant was ever
       tracked). Sold-out picks count too: the shopper looked at that size. */
    function announceVariantPick(variantId) {
      if (!variantId) return;
      var variant = null;
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(variantId)) {
          variant = variants[i];
          break;
        }
      }
      try {
        document.dispatchEvent(new CustomEvent('theme:pdp:variant-selected', {
          detail: { variantId: String(variantId), sku: variant && variant.sku ? variant.sku : '' },
        }));
      } catch (e) {
        /* CustomEvent unavailable — tracking is best effort */
      }
    }

    /* ---- Price follows the picked variant --------------------------------
       The buybox price ([data-pdp-price]), the gallery badge row
       (.spejder-pdp__badges — the SPAR-% badge) and the sticky-bar price are
       all Liquid, derived from current_variant in sections/spejder-pdp.liquid
       (sale + bundle compare, member, SS-pris, offer-end). Client-side size
       picks left all three at the LANDING variant's values, so a size with a
       different price showed the wrong amount until the cart (client card
       2026-09-22: KEEN Targhee 47 1/2 carries a 0 kr BC price — the PDP kept
       saying 1.499 kr). Rather than re-implement the price states in JS, the
       section is re-rendered for the picked variant (?variant=<id>&section_id=
       — the same server Liquid a page load with that variant runs) and the
       three nodes are swapped. Skipped, no request, when the picked variant's
       price and compare-at equal the rendered one — the common case. */
    var priceRenderedVariantId = variantIdInput ? String(variantIdInput.value || '') : '';
    var priceFetchSeq = 0;
    var PRICE_SWAP_SELECTORS = ['[data-pdp-price]', '.spejder-pdp__sticky-price'];

    function findVariantById(variantId) {
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(variantId)) return variants[i];
      }
      return null;
    }

    function priceApplyVariant(variantId) {
      if (!variantId || String(variantId) === priceRenderedVariantId) return;
      var next = findVariantById(variantId);
      var rendered = findVariantById(priceRenderedVariantId);
      if (next && rendered && next.price === rendered.price && next.compare_at_price === rendered.compare_at_price) {
        priceRenderedVariantId = String(variantId);
        return;
      }
      var sectionId = root.getAttribute('data-section-id');
      if (!sectionId || !window.fetch || !window.DOMParser) return;
      var seq = ++priceFetchSeq;
      /* Known limit: the section's landing rule (sections/spejder-pdp.liquid)
         swaps a sold-out ?variant= for the first available size, so a
         SOLD-OUT pick whose price differs from that size's re-renders with
         that size's price. Liquid cannot tell this fetch from a page load
         (no query-string access; /variants/<id>/ only exposes
         product_variant, not product, so the section cannot render there).
         Accepted 2026-09-28 — no published product had a sold-out size
         priced differently from its in-stock sizes. */
      fetch(window.location.pathname + '?variant=' + encodeURIComponent(variantId) + '&section_id=' + encodeURIComponent(sectionId))
        .then(function (res) { return res.ok ? res.text() : Promise.reject(res.status); })
        .then(function (html) {
          if (seq !== priceFetchSeq) return; // a later pick is in flight — its response wins
          var doc = new DOMParser().parseFromString(html, 'text/html');
          PRICE_SWAP_SELECTORS.forEach(function (selector) {
            var fresh = doc.querySelector(selector);
            var live = root.querySelector(selector);
            if (!fresh || !live) return;
            live.className = fresh.className;
            live.innerHTML = fresh.innerHTML; // [data-pdp-price] is aria-live — the new amount is announced
          });
          // The badge row only renders when there is a badge to show, so it
          // may need creating or removing, not just refreshing. Absolutely
          // positioned — DOM order inside [data-pdp-main] is irrelevant.
          var freshBadges = doc.querySelector('.spejder-pdp__badges');
          var liveBadges = root.querySelector('.spejder-pdp__badges');
          var main = root.querySelector('[data-pdp-main]');
          if (liveBadges && !freshBadges) liveBadges.parentNode.removeChild(liveBadges);
          else if (liveBadges && freshBadges) liveBadges.innerHTML = freshBadges.innerHTML;
          else if (freshBadges && main) main.insertBefore(freshBadges, main.firstChild);
          priceRenderedVariantId = String(variantId);
        })
        .catch(function () {
          /* Keep the rendered price — never invent one client-side. */
        });
    }

    /* Delivery row ("Fri fragt…" status): re-derive on client-side variant
       picks, mirroring the Liquid states in product-additional-buttons.liquid
       (green in stock / yellow low stock / red sold out — Figma 4634:39999).
       Texts ride data-ship-text-* on the row; the low text is absent where
       Liquid never goes yellow (second-hand), so low falls back to in-stock. */
    function shipApplyRowStatus(variantId) {
      var rowStatus = root.querySelector('[data-ship-row-status]');
      if (!rowStatus || !variantId) return;
      var variant = null;
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(variantId)) {
          variant = variants[i];
          break;
        }
      }
      if (!variant) return;
      var state = deriveState(variant);
      var rowDot = rowStatus.querySelector('[data-ship-row-dot]');
      var rowText = rowStatus.querySelector('[data-ship-row-text]');
      var lowText = rowStatus.getAttribute('data-ship-text-low');
      var text;
      var dotModifier = '';
      var isOos = state === 'sold-out' || state === 'unavailable';
      if (isOos) {
        text = rowStatus.getAttribute('data-ship-text-oos');
        dotModifier = ' spejder-pdp__status-dot--oos';
      } else if (state === 'low-stock' && lowText) {
        text = lowText;
        dotModifier = ' spejder-pdp__status-dot--low';
      } else {
        text = rowStatus.getAttribute('data-ship-text-in');
      }
      if (rowText) rowText.textContent = text || '';
      if (rowDot) rowDot.className = 'spejder-pdp__status-dot' + dotModifier;
      rowStatus.classList.toggle('spejder-pdp__row-status--oos', isOos);
    }

    /* ---- Notify me ------------------------------------------------------ */
    var notifyHost = root.querySelector('[data-pdp-notify-host]');
    var notifyVariantLineEl = notifyHost ? notifyHost.querySelector('[data-pdp-notify-variant-line]') : null;
    var notifyProductFieldEl = notifyHost ? notifyHost.querySelector('[data-pdp-notify-product-field]') : null;
    var notifyFormEl = notifyHost ? notifyHost.querySelector('[data-notification-form]') : null;

    function openNotify(variantLine, invoker, variantId) {
      if (notifyVariantLineEl) notifyVariantLineEl.textContent = variantLine || '';
      if (notifyProductFieldEl) notifyProductFieldEl.value = productTitleText + ' - ' + (variantLine || '');
      // Klaviyo subscribes per VARIANT (spejder-notify-klaviyo.js reads this),
      // so the id has to track the clicked size the same way the visible
      // product line does — mirrors quick-add-product.js's openNotifyOverlay.
      if (notifyFormEl && variantId) notifyFormEl.setAttribute('data-variant-id', variantId);
      try {
        window.sessionStorage.setItem('spejder_notify_variant_line', variantLine || '');
      } catch (e) {
        /* sessionStorage unavailable (private mode) — reopen path just
           won't restore the exact variant line; success/error state still
           works via product-notification.js's own ?contact_posted=true
           handling. */
      }
      openOverlay(notifyHost, invoker);
    }

    if (notifyHost) {
      root.querySelectorAll('[data-pdp-notify-trigger]').forEach(function (trigger) {
        trigger.addEventListener('click', function (e) {
          e.preventDefault();
          openNotify(trigger.getAttribute('data-variant-line'), trigger, trigger.getAttribute('data-variant-id'));
        });
      });

      // ?contact_posted=true reopen: product-notification.js already reopens
      // the dialog and announces success; restore the variant line we saved
      // before the native form's full-page redirect so the product card
      // still reads correctly instead of falling back to the page's default
      // current_variant.
      if (window.location.search.indexOf('contact_posted=true') !== -1) {
        try {
          var saved = window.sessionStorage.getItem('spejder_notify_variant_line');
          if (saved && notifyVariantLineEl) notifyVariantLineEl.textContent = saved;
        } catch (e) {
          /* noop */
        }

        /* Strip the contact_posted flag from the URL so the success-reopen
           fires exactly ONCE. product-notification.js's checkState() keys
           PURELY on this query param and re-runs on every connectedCallback,
           so while the param sticks in the URL the overlay reopens by itself:
           on reload, on back/forward, and every time the buy-box re-renders
           and the <product-notification> element reconnects. That file loads
           before this one, so checkState has already opened + announced
           success by now; we only need to clear the trigger for every
           subsequent load/reconnect. Also drop the #NotificationForm anchor
           so reloads don't scroll-jump to the form. */
        if (window.history && window.history.replaceState) {
          try {
            var params = new URLSearchParams(window.location.search);
            params.delete('contact_posted');
            var qs = params.toString();
            var hash = window.location.hash.indexOf('NotificationForm') !== -1 ? '' : window.location.hash;
            window.history.replaceState({}, document.title, window.location.pathname + (qs ? '?' + qs : '') + hash);
          } catch (e) {
            /* URLSearchParams/replaceState unavailable — leave the URL as-is */
          }
        }
        try { window.sessionStorage.removeItem('spejder_notify_variant_line'); } catch (e) { /* noop */ }
      }
    }

    /* ---- Size guide ------------------------------------------------------
       Card aP4qhkPI. Server-rendered (chart is resolved in Liquid — see
       spejder-size-guide-overlay.liquid), so the trigger just needs to open
       the shared drawer chrome; no client-side content fill required. */
    var sizeGuideHost = root.querySelector('[data-pdp-size-guide-host]');
    if (sizeGuideHost) {
      root.querySelectorAll('[data-size-guide-trigger]').forEach(function (trigger) {
        trigger.addEventListener('click', function (e) {
          e.preventDefault();
          openOverlay(sizeGuideHost, trigger);
        });
      });
    }

    /* ---- Shipping + Returns content drawers (card wmTwAuGg) --------------
       Static content overlays (no per-variant state) — every trigger just
       opens its overlay via the shared openOverlay() proxy/focus-return
       helper, same as Notify-me / Select-size above. */
    var shippingHost = root.querySelector('[data-pdp-shipping-host]');
    if (shippingHost) {
      root.querySelectorAll('[data-shipping-trigger]').forEach(function (trigger) {
        trigger.addEventListener('click', function (e) {
          e.preventDefault();
          openOverlay(shippingHost, trigger);
        });
      });
    }

    var returnsHost = root.querySelector('[data-pdp-returns-host]');
    if (returnsHost) {
      root.querySelectorAll('[data-returns-trigger]').forEach(function (trigger) {
        trigger.addEventListener('click', function (e) {
          e.preventDefault();
          openOverlay(returnsHost, trigger);
        });
      });
    }

    /* ---- Compact buybox rows: Farve swap re-derives Størrelse states ---- */
    function refreshCompactPills(colorValue) {
      if (!sizePillsEl) return;
      sizePillsEl.querySelectorAll('[data-pdp-size-pill]').forEach(function (pill) {
        var value = pill.getAttribute('data-value');
        var variant = findVariant(value, colorValue);
        var state = deriveState(variant);
        var line = colorPosition ? colorValue + ' | ' + value : value;

        pill.className = 'spejder-pdp__size-pill is-' + state;
        pill.setAttribute('data-state', state);
        pill.setAttribute('data-variant-id', variant ? variant.id : '');
        pill.setAttribute('data-variant-line', line);
        pill.removeAttribute('data-pdp-notify-trigger');
        pill.removeAttribute('data-pdp-size-pill-select');
        pill.removeAttribute('aria-pressed');
        pill.removeAttribute('aria-label');
        pill.removeAttribute('aria-disabled');
        pill.removeAttribute('tabindex');

        if (state === 'sold-out') {
          // Selectable like any pill (Figma 5721:157457) — selecting swaps
          // the CTA to "Skriv mig op"; the overlay opens from that CTA.
          pill.setAttribute('data-pdp-size-pill-select', '');
          pill.setAttribute('aria-pressed', 'false');
          pill.setAttribute('aria-label', value + ' — Udsolgt');
        } else if (state === 'available' || state === 'low-stock') {
          pill.setAttribute('data-pdp-size-pill-select', '');
          pill.setAttribute('aria-pressed', 'false');
        } else {
          pill.setAttribute('aria-disabled', 'true');
          pill.setAttribute('tabindex', '-1');
        }
      });
      bindDynamicPillTriggers();
    }

    function updateAtcGateForColorOnly() {
      // A bare colour change never resolves a full variant by itself
      // (size is still unset) — clear any prior size selection so the ATC
      // guard re-engages until a size row is picked again for the new
      // colour. refreshCompactPills() already rebuilds pill classNames
      // (dropping any stale .is-selected), so only the shared state +
      // hidden variant id need resetting here.
      sizeExplicitlyChosen = false;
      if (variantIdInput) {
        variantIdInput.value = '';
        variantIdInput.disabled = true;
      }
    }

    if (colorSwatchesEl) {
      colorSwatchesEl.querySelectorAll('[data-pdp-color-swatch]').forEach(function (swatch) {
        swatch.addEventListener('click', function () {
          var value = swatch.getAttribute('data-value');
          colorSwatchesEl.querySelectorAll('[data-pdp-color-swatch]').forEach(function (s) {
            var isSelected = s === swatch;
            s.classList.toggle('is-selected', isSelected);
            s.setAttribute('aria-checked', String(isSelected));
          });
          var labelEl = root.querySelector('[data-pdp-color-selected]');
          if (labelEl) labelEl.textContent = value;
          refreshCompactPills(value);
          updateAtcGateForColorOnly();
        });
      });
    }

    /* ---- Pricematch content drawer (card ZoQGnp39, B1) -----------------
       Simple content drawer, single trigger (the buybox pill) — no variant
       logic. Same host + hidden-internal-proxy convention as Notify-me /
       Select-size above so they all share openOverlay(). (Its Buyback twin
       was removed with the Tilbagekøbsløftet pill, card WhqmDUvt.) ---- */
    var pricematchHost = root.querySelector('[data-pdp-pricematch-host]');
    if (pricematchHost) {
      root.querySelectorAll('[data-pdp-pricematch-trigger]').forEach(function (trigger) {
        trigger.addEventListener('click', function (e) {
          e.preventDefault();
          openOverlay(pricematchHost, trigger);
        });
      });
    }

    /* ---- "Om produktet" bundle-item detail drawer (card 1NIULYM3, B2) ----
       Same host/proxy convention as every overlay above, but the content
       is per-PRODUCT: each bundle item's full drawer body was already
       server-rendered into its own hidden <template> (see
       spejder-pdp-om-produktet-overlay.liquid) so the money/sale/save/
       description HTML never has to be recomputed in JS — this just
       clones the template matching the clicked chevron's
       data-product-handle into the single live mount, then re-runs the
       SAME gallery/read-more/wishlist init functions the main PDP uses
       (window.theme.spejderPdp, exposed by spejder-pdp.js) against that
       freshly-cloned markup. No second gallery/toggle/wishlist
       implementation (checklist DRY gates).

       The chevron itself is an intentionally INERT STUB in
       spejder-pdp-bundle.liquid (aria-disabled + tabindex="-1", per that
       file's own "wire it up without touching this markup" contract) —
       activated here, at runtime, only when this drawer's host actually
       rendered (i.e. only on real bundle products; regression-safe). ---- */
    var omProduktetHost = root.querySelector('[data-pdp-om-produktet-host]');
    var omProduktetMount = omProduktetHost ? omProduktetHost.querySelector('[data-om-produktet-mount]') : null;

    // Fills the drawer with one product's template. `slot` is passed when the
    // add-to-cart flow opens it for a bundle product without a size.
    function fillOmProduktet(handle, variantId, invoker, slot) {
      if (!omProduktetHost || !omProduktetMount || !handle) return false;
      var templates = omProduktetHost.querySelectorAll('template[data-om-produktet-template]');
      // Templates are keyed by handle + variant id (a bundle can hold two
      // VARIANTS of the same product, e.g. two shoe sizes — handle alone is
      // ambiguous there). Prefer the exact variant template, fall back to
      // the first template for the product (legacy product-only entries).
      var match = null;
      var fallback = null;
      templates.forEach(function (tpl) {
        if (tpl.getAttribute('data-product-handle') !== handle) return;
        if (!fallback) fallback = tpl;
        if (variantId && tpl.getAttribute('data-variant-id') === variantId && !match) match = tpl;
      });
      match = match || fallback;
      if (!match) return false;

      omProduktetMount.innerHTML = '';
      omProduktetMount.appendChild(match.content.cloneNode(true));
      bundleOmPatch(omProduktetMount, invoker, slot);

      if (window.theme && window.theme.spejderPdp) {
        window.theme.spejderPdp.initGallery(omProduktetMount);
        window.theme.spejderPdp.initReadMore(omProduktetMount);
        window.theme.spejderPdp.initWishlist(omProduktetMount);
      }
      return true;
    }

    /* Teaser fills the drawer's free height (Emil 2026-08-13): compute how
       many text lines fit between the teaser's top and the drawer body's
       bottom, set the clamp to exactly that, and only show Læs mere when
       the description truly overflows it. Measured once the drawer has real
       geometry (CSS keeps a 3-line no-JS fallback). */
    function clampOmTeaser() {
      var descEl = omProduktetMount.querySelector('[data-toggle-ellipsis]');
      if (!descEl || descEl.classList.contains('is-expanded')) return;
      var avail =
        omProduktetMount.getBoundingClientRect().bottom -
        descEl.getBoundingClientRect().top -
        32; /* room for the Læs mere row */
      var lh = parseFloat(getComputedStyle(descEl).lineHeight);
      if (!lh || isNaN(lh)) lh = 20;
      var lines = Math.max(3, Math.floor(avail / lh));
      descEl.style.webkitLineClamp = String(lines);
      descEl.style.lineClamp = String(lines);
      var readMoreBtn = omProduktetMount.querySelector('[data-toggle-ellipsis-trigger]');
      if (readMoreBtn) {
        readMoreBtn.hidden = !(descEl.scrollHeight - descEl.clientHeight > 1);
      }
    }

    function openOmProduktet(handle, variantId, invoker, slot) {
      if (!fillOmProduktet(handle, variantId, invoker, slot)) return;
      openOverlay(omProduktetHost, invoker);
      requestAnimationFrame(clampOmTeaser);
    }

    if (omProduktetHost) {
      root.querySelectorAll('[data-bundle-drawer-trigger]').forEach(function (chevron) {
        // Flip the stub live: it ships inert (aria-disabled + tabindex="-1")
        // until this drawer's host actually exists on the page.
        chevron.removeAttribute('aria-disabled');
        chevron.removeAttribute('tabindex');
        chevron.classList.add('is-active');

        // Whole row is clickable (bigger hit target), but the invoker
        // passed to openOverlay() is always the chevron BUTTON itself —
        // that's the focusable element focus must return to on close. A
        // product the customer picks a size for opens here too, with its
        // sizes under the price (bundleOmPatch, Magnus 2026-10-19).
        var row = chevron.closest('.spejder-pdp__bundle-row') || chevron;
        row.addEventListener('click', function (e) {
          e.preventDefault();
          openOmProduktet(chevron.getAttribute('data-product-handle'), chevron.getAttribute('data-variant-id'), chevron);
        });
      });
    }

    /* ---- Click & Collect store-directory drawer (card HsOPbWeH, Unit 1) --
       Static content drawer (store list is server-rendered once from the
       `store` metaobject) — the only interactive bits are client-side:
       search-as-you-type over the pre-rendered cards, an opening-hours
       accordion per card, and a single-select radio per card. Live
       stock/geodistance stays split out to Unit 2 (card 2NfK409N); this
       only toggles which already-rendered state is shown. The trigger row
       additionally needs aria-expanded kept in sync with the dialog (per
       checklist), which none of the sibling static-content triggers above
       require — handled locally here rather than generalizing openOverlay/
       closeOverlay for a need only this drawer has. */
    var clickCollectHost = root.querySelector('[data-pdp-click-collect-host]');
    if (clickCollectHost) {
      var ccTriggers = root.querySelectorAll('[data-click-collect-trigger]');
      var ccDialog = clickCollectHost.querySelector('dialog');

      /* Live stock state (option A, Shopify pickup availability): the snippet
         server-renders each card for the initial variant and embeds that
         variant's {available, pickup: {location id: bool}} entry plus the
         states' labels/colours; other variants are lazily fetched (see
         ccFetchEntry below) and the cards/row re-derived from the cached
         entry. */
      var ccAvailability = null;
      var ccConfig = null;
      try {
        var ccAvailEl = clickCollectHost.querySelector('[data-cc-availability]');
        var ccConfigEl = clickCollectHost.querySelector('[data-cc-config]');
        ccAvailability = ccAvailEl ? JSON.parse(ccAvailEl.textContent) : null;
        ccConfig = ccConfigEl ? JSON.parse(ccConfigEl.textContent) : null;
      } catch (err) {
        ccAvailability = null;
        ccConfig = null;
      }

      /* Klik & Hent row ("På lager i [count] butikker…"): mirror the live
         count into the retail-info row whenever a variant is picked. The row
         is server-rendered for the initial variant; this keeps it honest for
         client-side variant switches, including sold-out picks (which never
         reach variantIdInput — hence the explicit variantId parameter). */
      /* The embedded map only ever holds entries we KNOW are true: the
         server render seeds the initially selected variant, every other
         variant is fetched from /variants/<id>/?section_id=
         api-spejder-pickup on first pick and cached for the page's
         lifetime. (Liquid populates store_availabilities solely for the
         selected variant — an all-variants map server-side would bake in
         "pickup nowhere" for every other size.) */
      var ccFetchSeq = 0;
      function ccFetchEntry(variantId, onDone, onError) {
        var rootUrl = (window.theme && window.theme.routes && window.theme.routes.root) || '/';
        if (rootUrl.charAt(rootUrl.length - 1) !== '/') rootUrl += '/';
        fetch(rootUrl + 'variants/' + variantId + '/?section_id=api-spejder-pickup')
          .then(function (res) { return res.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var el = doc.querySelector('[data-spejder-pickup]');
            if (!el) { if (onError) onError(); return; }
            var data = JSON.parse(el.textContent);
            var entry = { available: data.available, pickup: data.pickup || {} };
            ccAvailability[String(data.variantId)] = entry;
            if (onDone) onDone(entry);
          })
          .catch(function () {
            /* Leave the row/cards as they are — better a stale state than a
               wrong "Udsolgt i butik" invented from a failed request. */
            if (onError) onError();
          });
      }

      /* Bundle: pickup for the untracked parent is meaningless, so the row
         and the drawer use the AND of every component's own pickup map — a
         store can hand over the bundle only when it holds all of its parts.
         A sold-out bundle (a component at 0 across all locations) is pickup
         nowhere without asking. Cached under BUNDLE_KEY; a failed component
         fetch leaves the pending/previous state rather than inventing one. */
      var BUNDLE_KEY = 'bundle';
      function ccBundleEntry(onDone) {
        /* A bundle with a size per product: a store holds it when it can
           send a whole bundle in the sizes picked so far (any size for a
           product without a pick), counted from the app's stock map, which
           changes with every pick, so nothing is cached. Without the map
           the row stays hidden. */
        if (bundleChoiceSlots.length) {
          if (!bundleStock) return;
          var choiceEntry = { available: bundleSetsTotal(null) > 0, pickup: {} };
          clickCollectHost.querySelectorAll('[data-cc-card]').forEach(function (card) {
            var locationId = card.getAttribute('data-cc-location-id');
            if (locationId) choiceEntry.pickup[locationId] = bundleSetsAt(locationId, null) >= 1;
          });
          onDone(choiceEntry);
          return;
        }
        if (ccAvailability[BUNDLE_KEY]) { onDone(ccAvailability[BUNDLE_KEY]); return; }
        if (bundleSoldOut || !bundleComponentIds.length) {
          ccAvailability[BUNDLE_KEY] = { available: !bundleSoldOut, pickup: {} };
          onDone(ccAvailability[BUNDLE_KEY]);
          return;
        }
        var pending = bundleComponentIds.length;
        var failed = false;
        var entries = [];
        var settle = function (entry) {
          if (entry) entries.push(entry); else failed = true;
          if (--pending > 0 || failed) return;
          var combined = { available: true, pickup: {} };
          Object.keys(entries[0].pickup || {}).forEach(function (locationId) {
            combined.pickup[locationId] = entries.every(function (e) { return !!(e.pickup && e.pickup[locationId]); });
          });
          ccAvailability[BUNDLE_KEY] = combined;
          onDone(combined);
        };
        bundleComponentIds.forEach(function (id) {
          var cached = ccAvailability[String(id)];
          if (cached) settle(cached);
          else ccFetchEntry(id, settle, function () { settle(null); });
        });
      }

      /* State follows STORE stock alone (Figma 4634:39999 v4/12 pair a web
         "Udsolgt." with a live store count — web OOS must not force this row
         red); entry.available only picks the count-0 wording (rowNone =
         "Udsolgt i butik" while the web still sells vs rowOos when the
         variant is fully unavailable). Mirrors the Liquid derivation in
         product-additional-buttons.liquid. */
      function ccRenderRowStatus(entry) {
        var rowStatus = root.querySelector('[data-cc-row-status]');
        if (!entry || !rowStatus) return;
        var rowDot = rowStatus.querySelector('[data-cc-row-dot]');
        var rowText = rowStatus.querySelector('[data-cc-row-text]');
        var count = 0;
        if (entry.pickup) {
          Object.keys(entry.pickup).forEach(function (id) {
            if (entry.pickup[id]) count += 1;
          });
        }
        var fewThreshold = Number(ccConfig.rowFewThreshold) || 0;
        var text;
        var dotModifier = '';
        if (count === 0) {
          text = entry.available ? ccConfig.rowNone : ccConfig.rowOos;
          dotModifier = ' spejder-pdp__status-dot--oos';
        } else if (count === 1) {
          text = ccConfig.rowOne;
          if (fewThreshold >= 1) dotModifier = ' spejder-pdp__status-dot--low';
        } else if (count <= fewThreshold) {
          text = ccConfig.rowFew.replace('[count]', String(count));
          dotModifier = ' spejder-pdp__status-dot--low';
        } else {
          text = ccConfig.rowMany.replace('[count]', String(count));
        }
        if (rowText) rowText.textContent = text || '';
        if (rowDot) rowDot.className = 'spejder-pdp__status-dot' + dotModifier;
        rowStatus.classList.toggle('spejder-pdp__row-status--oos', count === 0);
        // Bundles server-render this line hidden (product-additional-buttons)
        // until the components' maps have been combined — reveal it now.
        rowStatus.hidden = false;
        rowStatus.removeAttribute('data-cc-row-pending');
      }

      /* The size the row describes, and so the drawer too: the last pick,
         sold-out picks included. Those never reach variantIdInput (the CTA
         turns into "Skriv mig op" instead), so a drawer reading the input
         stayed on the previous size (client card 6eYrsgEx, 2026-10-29: 58
         sold out, row "Ikke på lager i butik", drawer listed the stores
         holding the landing size). */
      var ccVariantId = variantIdInput ? String(variantIdInput.value || '') : '';

      ccApplyRowStatus = function (variantId) {
        if (!ccAvailability || !ccConfig) return;
        if (isBundle) {
          var bundleSeq = ++ccFetchSeq;
          ccBundleEntry(function (entry) {
            if (bundleSeq !== ccFetchSeq) return;
            ccRenderRowStatus(entry);
          });
          return;
        }
        if (!variantId) return;
        ccVariantId = String(variantId);
        var entry = ccAvailability[String(variantId)];
        if (entry) {
          ccRenderRowStatus(entry);
          return;
        }
        /* Seq guard: on rapid size flicking only the LAST pick may paint. */
        var seq = ++ccFetchSeq;
        ccFetchEntry(variantId, function (fresh) {
          if (seq !== ccFetchSeq) return;
          ccRenderRowStatus(fresh);
        });
      };

      // A bundle's row is derived on load (its server render is pending).
      if (isBundle) ccApplyRowStatus(null);

      function ccApplyStockState() {
        if (!ccAvailability || !ccConfig) return;
        if (isBundle) {
          ccBundleEntry(function (entry) { ccRenderStockCards(entry); });
          return;
        }
        if (!ccVariantId) return;
        var variantId = ccVariantId;
        var entry = ccAvailability[variantId];
        if (entry) {
          ccRenderStockCards(entry);
          return;
        }
        ccFetchEntry(variantId, function (fresh) {
          /* Drawer may sit open while the user flips sizes behind it —
             only paint if this is still the active variant. */
          if (ccVariantId !== variantId) return;
          ccRenderStockCards(fresh);
        });
      }

      function ccRenderStockCards(entry) {
        clickCollectHost.querySelectorAll('[data-cc-card]').forEach(function (card) {
          var locationId = card.getAttribute('data-cc-location-id');
          var state = 'out_of_stock';
          if (entry.pickup && entry.pickup[locationId]) {
            state = 'in_stock';
          } else if (entry.available) {
            state = 'web_stock';
          }
          var dot = card.querySelector('[data-cc-dot]');
          var label = card.querySelector('[data-cc-stock-label]');
          var badge = card.querySelector('[data-cc-badge]');
          if (dot) {
            dot.className = 'spejder-cc__dot spejder-cc__dot--' + state;
            dot.style.backgroundColor = state === 'in_stock' ? ccConfig.dotIn : state === 'web_stock' ? ccConfig.dotWeb : ccConfig.dotOut;
          }
          if (label) label.textContent = state === 'in_stock' ? ccConfig.inLabel : state === 'web_stock' ? ccConfig.webLabel : ccConfig.outLabel;
          if (badge) badge.textContent = state === 'in_stock' ? ccConfig.badgeIn : ccConfig.badgeOther;
        });
      }

      ccTriggers.forEach(function (trigger) {
        trigger.addEventListener('click', function (e) {
          e.preventDefault();
          ccApplyStockState();
          openOverlay(clickCollectHost, trigger);
          ccTriggers.forEach(function (t) {
            t.setAttribute('aria-expanded', 'true');
          });
        });
      });
      if (ccDialog) {
        ccDialog.addEventListener('close', function () {
          ccTriggers.forEach(function (t) {
            t.setAttribute('aria-expanded', 'false');
          });
        });
      }

      var ccSearchInput = clickCollectHost.querySelector('[data-cc-search]');
      var ccCards = clickCollectHost.querySelectorAll('[data-cc-card]');
      var ccEmpty = clickCollectHost.querySelector('[data-cc-empty]');

      if (ccSearchInput) {
        ccSearchInput.addEventListener('input', function () {
          var query = ccSearchInput.value.trim().toLowerCase();
          var visibleCount = 0;
          ccCards.forEach(function (card) {
            var haystack = card.getAttribute('data-cc-search-text') || '';
            var matches = query === '' || haystack.indexOf(query) !== -1;
            card.hidden = !matches;
            if (matches) visibleCount += 1;
          });
          if (ccEmpty) ccEmpty.hidden = visibleCount !== 0;
        });
      }

      clickCollectHost.querySelectorAll('[data-cc-hours-toggle]').forEach(function (toggle) {
        toggle.addEventListener('click', function () {
          var rows = toggle.parentElement.querySelector('[data-cc-hours-rows]');
          var expanded = toggle.getAttribute('aria-expanded') === 'true';
          toggle.setAttribute('aria-expanded', String(!expanded));
          if (rows) rows.classList.toggle('is-open', !expanded);
        });
      });

      var ccRadios = clickCollectHost.querySelectorAll('[data-cc-select]');
      // The whole card selects its store (the radio stays the keyboard/AT
      // control); clicks inside the opening-hours toggle/rows and on the
      // phone link (a call, not a pick) are left alone.
      ccRadios.forEach(function (radio) {
        radio.closest('[data-cc-card]').addEventListener('click', function (event) {
          if (event.target.closest('[data-cc-hours-toggle], [data-cc-hours-rows], [data-cc-phone]')) return;
          ccRadios.forEach(function (r) {
            var isSelected = r === radio;
            r.setAttribute('aria-checked', String(isSelected));
            r.closest('[data-cc-card]').classList.toggle('is-selected', isSelected);
          });
        });
      });
    }

    /* ---- Select size overlay --------------------------------------------- */
    var selectSizeHost = root.querySelector('[data-pdp-select-size-host]');
    var selectSizeRowsEl = root.querySelector('[data-pdp-select-size-rows]');
    var selectSizeHeadingEl = root.querySelector('[data-pdp-select-size-heading]');
    var rowTemplate = root.querySelector('[data-pdp-select-size-row-template]');
    var currentSelectSizeInvoker = null;

    function currentSelectedColor() {
      var labelEl = root.querySelector('[data-pdp-color-selected]');
      return labelEl ? labelEl.textContent.trim() : null;
    }

    // Reflects a resolved size value back onto the inline buybox pills
    // (dark-green .is-selected fill) — used both when the inline pill
    // itself is clicked and when a size is resolved via the select-size
    // guard overlay, so the two entry points never fall out of sync.
    function syncInlinePillSelection(value) {
      if (!sizePillsEl) return;
      sizePillsEl.querySelectorAll('[data-pdp-size-pill]').forEach(function (p) {
        var isSelected = p.getAttribute('data-value') === value;
        p.classList.toggle('is-selected', isSelected);
        p.setAttribute('aria-pressed', String(isSelected));
      });
      // Figma label flip (4634:37907 -> 5721:155248): "Vælg størrelse"
      // becomes "Størrelse: S" once a size is resolved. Condition rows
      // ("Varens stand") carry data-static-label and never change.
      var labelEl = root.querySelector('[data-pdp-size-label][data-option-name="' + optionName + '"]:not([data-static-label])');
      if (labelEl) {
        var textEl = labelEl.querySelector('[data-pdp-size-label-text]');
        var valueEl = labelEl.querySelector('[data-pdp-size-label-value]');
        var infoEl = labelEl.querySelector('[data-pdp-size-label-info]');
        if (textEl && valueEl && value) {
          textEl.textContent = optionName + ':';
          valueEl.textContent = value;
          valueEl.hidden = false;
        }
        // "Størrelse: M | Udsolgt" (Figma 5721:157457 Indicator).
        if (infoEl) {
          var selPill = sizePillsEl.querySelector('[data-pdp-size-pill].is-selected');
          var soldOut = selPill && selPill.getAttribute('data-state') === 'sold-out';
          infoEl.textContent = soldOut ? '| ' + (labelEl.getAttribute('data-soldout-label') || 'Udsolgt') : '';
          infoEl.hidden = !soldOut;
        }
      }
    }

    // CTA pair swap (Figma 5721:157457): a resolved SOLD-OUT size hides the
    // green add-to-cart and reveals the dark "Skriv mig op" button (both are
    // server-rendered; this mirrors the section's own visually-hidden gating).
    var notifyCtaBtn = root.querySelector('[data-pdp-notify-cta]');
    // Bundle sold-out (custom.bundle_availability) is a WHOLE-PRODUCT state:
    // the parent variant is inventory-untracked so every variant reads as
    // available client-side — no size pick may flip the CTA back to buy.
    // bundleSoldOut is resolved once at the top of initPDP (data-bundle-*).
    function setCtaMode(mode, variantId, variantLine) {
      if (!addToCartBtn || !notifyCtaBtn) return;
      if (bundleSoldOut) mode = 'notify';
      var notify = mode === 'notify';
      addToCartBtn.classList.toggle('visually-hidden', notify);
      addToCartBtn.disabled = notify;
      if (notify) addToCartBtn.setAttribute('tabindex', '-1');
      else addToCartBtn.removeAttribute('tabindex');
      notifyCtaBtn.classList.toggle('visually-hidden', !notify);
      notifyCtaBtn.disabled = !notify;
      if (notify) {
        notifyCtaBtn.removeAttribute('tabindex');
        if (variantId) notifyCtaBtn.setAttribute('data-variant-id', variantId);
        if (variantLine) notifyCtaBtn.setAttribute('data-variant-line', variantLine);
      } else {
        notifyCtaBtn.setAttribute('tabindex', '-1');
      }
    }

    function buildSelectSizeRows() {
      if (!selectSizeRowsEl || !rowTemplate || !sizePillsEl) return;
      selectSizeRowsEl.innerHTML = '';
      // Figma header (5718:155014) reads "Vælg størrelse", not the bare
      // option name — keep the server-rendered translation for any
      // størrelse-type option and only compose "Vælg <option>" for
      // non-size option names (e.g. second-hand condition rows).
      if (selectSizeHeadingEl && optionName && !/størrelse/i.test(optionName)) {
        selectSizeHeadingEl.textContent = 'Vælg ' + optionName.toLowerCase();
      }

      sizePillsEl.querySelectorAll('[data-pdp-size-pill]').forEach(function (pill) {
        var frag = rowTemplate.content.cloneNode(true);
        var rowBtn = frag.querySelector('[data-pdp-select-size-row]');
        var value = pill.getAttribute('data-value');
        var state = pill.getAttribute('data-state');
        var variantId = pill.getAttribute('data-variant-id');
        var variantLine = pill.getAttribute('data-variant-line');

        rowBtn.querySelector('.spejder-select-size__row-label').textContent = value;
        rowBtn.classList.add('is-' + state);
        rowBtn.setAttribute('data-value', value);
        rowBtn.setAttribute('data-state', state);
        rowBtn.setAttribute('data-variant-id', variantId || '');
        rowBtn.setAttribute('data-variant-line', variantLine || '');
        rowBtn.setAttribute('aria-selected', 'false');
        if (state === 'unavailable') {
          rowBtn.setAttribute('aria-disabled', 'true');
          rowBtn.setAttribute('tabindex', '-1');
        }

        selectSizeRowsEl.appendChild(frag);
      });

      bindSelectSizeRowClicks();
    }

    function bindSelectSizeRowClicks() {
      selectSizeRowsEl.querySelectorAll('[data-pdp-select-size-row]').forEach(function (row) {
        row.addEventListener('click', function () {
          var state = row.getAttribute('data-state');
          if (state === 'unavailable') return;

          if (state === 'sold-out') {
            // Chain into Notify-me — reassign the return-focus invoker to
            // whatever opened select-size (this row is about to be gone),
            // per the checklist's "reassigned-invoker focus return". The
            // close is animated/async (popup-component defers the actual
            // dialog.close() until its CSS transition ends), so wait for
            // the dialog's native `close` event before opening Notify —
            // opening immediately races select-size's own close-time
            // autoFocusLastElement() and both overlays fight over focus.
            var chainInvoker = currentSelectSizeInvoker;
            var chainVariantLine = row.getAttribute('data-variant-line');
            var dialogEl = selectSizeHost.querySelector('dialog');
            var onClosed = function () {
              dialogEl.removeEventListener('close', onClosed);
              openNotify(chainVariantLine, chainInvoker);
            };
            dialogEl.addEventListener('close', onClosed);
            closeOverlay(selectSizeHost);
            return;
          }

          // Available / low-stock: this overlay only ever opens as the
          // no-size-selected GUARD on an actual Add-to-cart click (see the
          // ATC listener below) — so resolving a size here completes that
          // original intent: set the real variant, reflect the pick on the
          // inline pill, and submit the real add-to-cart form immediately
          // (no manual second ATC click).
          var value = row.getAttribute('data-value');
          var variantId = row.getAttribute('data-variant-id');
          if (variantIdInput && variantId) {
            variantIdInput.value = variantId;
            variantIdInput.disabled = false;
          }
          if (ccApplyRowStatus) ccApplyRowStatus(variantId);
          shipApplyRowStatus(variantId);
          priceApplyVariant(variantId);
          announceVariantPick(variantId);
          if (addToCartBtn) {
            addToCartBtn.classList.remove('visually-hidden');
            addToCartBtn.disabled = false;
            addToCartBtn.removeAttribute('tabindex');
          }
          syncInlinePillSelection(value);
          sizeExplicitlyChosen = true;
          closeOverlay(selectSizeHost);
          if (addToCartForm && typeof addToCartForm.requestSubmit === 'function') {
            addToCartForm.requestSubmit(addToCartBtn);
          } else if (addToCartBtn) {
            addToCartBtn.click();
          }
        });
      });
    }

    function openSelectSize(invoker) {
      if (!selectSizeHost) return;
      buildSelectSizeRows();
      currentSelectSizeInvoker = invoker;
      openOverlay(selectSizeHost, invoker);
    }

    // Inline pill click just SELECTS the size (dark-green .is-selected
    // fill) — no overlay. Add-to-cart still has to be clicked separately
    // to actually add (human-verified correction: the overlay is a
    // no-size-selected GUARD, not a gate on every ATC click).
    function selectInlineSize(pill) {
      var value = pill.getAttribute('data-value');
      var variantId = pill.getAttribute('data-variant-id');
      var state = pill.getAttribute('data-state');
      syncInlinePillSelection(value);
      if (ccApplyRowStatus) ccApplyRowStatus(variantId);
      shipApplyRowStatus(variantId);
      priceApplyVariant(variantId);
      announceVariantPick(variantId);
      if (state === 'sold-out') {
        // Figma 5721:157457: the pill selects; the CTA becomes "Skriv mig op"
        // and owns opening the Notify overlay.
        setCtaMode('notify', variantId, pill.getAttribute('data-variant-line'));
        sizeExplicitlyChosen = false;
        return;
      }
      setCtaMode('buy');
      if (variantIdInput && variantId) {
        variantIdInput.value = variantId;
        variantIdInput.disabled = false;
      }
      sizeExplicitlyChosen = true;
    }

    function bindDynamicPillTriggers() {
      if (!sizePillsEl) return;
      sizePillsEl.querySelectorAll('[data-pdp-size-pill-select]').forEach(function (trigger) {
        // Guard against double-binding across refreshCompactPills() calls.
        if (trigger.__spejderBound) return;
        trigger.__spejderBound = true;
        trigger.addEventListener('click', function (e) {
          e.preventDefault();
          selectInlineSize(trigger);
        });
      });
    }

    /* ---- Bundle: a size per product (client card UbNVzlvf, 2026-10-16) ---
       A row with "Vælg størrelse" (spejder-pdp-bundle.liquid) opens "Om
       produktet" like any bundle row, and there the product page's own size
       label and buttons sit under the price (bundleOmRender; option A, Magnus
       2026-10-19: the description stays next to the size choice). Each size
       is in the state the bundle would have with it; a sold-out size can't be
       picked, and there is no restock notice for one size of a bundle, so no
       bell either. A pick writes "Størrelse: M" into the row and the
       "<slot>:<variant id>" list into the hidden _bundle_choices property,
       which the app's cart transform checks against the same list before it
       splits the bundle into those sizes. Add to cart with a product still
       without a size opens the same drawer on it; a pick there moves on to
       the next product without a size, in the open drawer, and the bundle is
       added once every product has its size (Magnus 2026-10-19: the same
       drawer as a click on the row). */
    var bundleChoicesInput = root.querySelector('[data-bundle-choices-input]');
    var bundleChain = false;

    function bundlePickState(slot, variantId) {
      var total = bundleStock ? bundleSetsTotal({ slot: slot, id: variantId }) : bundleSoldOut ? 0 : bundleMaxQty;
      if (total !== null && total <= 0) return 'sold-out';
      if (total !== null && total <= lowStockThreshold) return 'low-stock';
      return 'available';
    }

    function bundleNextUnpicked() {
      for (var i = 0; i < bundleChoiceSlots.length; i++) {
        var slot = bundleChoiceSlots[i];
        if (slot.row && !bundlePicked[slot.key]) return slot;
      }
      return null;
    }

    function bundlePick(slot, choice) {
      bundlePicked[slot.key] = choice.id;
      if (slot.pickBtn) slot.pickBtn.textContent = (slot.option || 'Størrelse') + ': ' + choice.value;
      if (slot.chevron) slot.chevron.setAttribute('data-variant-id', choice.id);
      if (bundleChoicesInput) {
        bundleChoicesInput.value = bundleChoiceSlots
          .filter(function (s) { return bundlePicked[s.key]; })
          .map(function (s) { return s.key + ':' + bundlePicked[s.key]; })
          .join(',');
      }
      // The delivery and Klik & Hent rows follow the sets the picks leave.
      if (bundleStock) {
        var total = bundleSetsTotal(null);
        bundleMaxQty = total === Infinity ? null : total;
      }
      if (variantIdInput) shipApplyRowStatus(variantIdInput.value);
      if (ccApplyRowStatus) ccApplyRowStatus(null);
    }

    // "Om produktet" for a product with a size pick (one template per
    // product, spejder-pdp-om-produktet-overlay.liquid): its sizes under the
    // price, as the product page's size label and buttons, and the picked
    // size's SKU and link. `slot` comes with the add-to-cart flow; a click on
    // a row finds it from the row.
    function bundleOmPatch(mount, invoker, slot) {
      if (!slot) {
        var row = invoker && invoker.closest ? invoker.closest('[data-bundle-choice]') : null;
        bundleChoiceSlots.forEach(function (s) {
          if (row && s.row === row) slot = s;
        });
      }
      if (slot) bundleOmRender(mount, slot);
    }

    function bundleOmRender(mount, slot) {
      var choice = null;
      slot.choices.forEach(function (c) {
        if (c.id === bundlePicked[slot.key]) choice = c;
      });
      var skuEl = mount.querySelector('[data-om-sku]');
      if (skuEl) {
        skuEl.textContent = choice ? choice.sku : '';
        skuEl.hidden = !(choice && choice.sku);
      }
      mount.querySelectorAll('.spejder-om__gallery-link, .spejder-om__title-link').forEach(function (link) {
        var base = (link.getAttribute('href') || '').split('?')[0];
        link.setAttribute('href', choice ? base + '?variant=' + choice.id : base);
      });

      var box = mount.querySelector('[data-om-sizes]');
      if (!box) return;
      box.innerHTML = '';
      box.hidden = false;
      var label = document.createElement('div');
      label.className = 'spejder-pdp__variant-label';
      var labelText = document.createElement('span');
      labelText.className = 'spejder-pdp__variant-label-text';
      label.appendChild(labelText);
      if (choice) {
        labelText.textContent = (slot.option || 'Størrelse') + ':';
        var labelValue = document.createElement('span');
        labelValue.className = 'spejder-pdp__variant-label-value';
        labelValue.textContent = choice.value;
        label.appendChild(labelValue);
      } else {
        labelText.textContent = slot.pickLabel;
      }
      var pills = document.createElement('div');
      pills.className = 'spejder-pdp__size-pills';
      pills.setAttribute('role', 'group');
      pills.setAttribute('aria-label', slot.option || 'Størrelse');
      slot.choices.forEach(function (c) {
        var state = bundlePickState(slot, c.id);
        var picked = choice === c;
        var pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'spejder-pdp__size-pill is-' + state + (picked ? ' is-selected' : '');
        pill.setAttribute('aria-pressed', String(picked));
        var pillLabel = document.createElement('span');
        pillLabel.className = 'spejder-pdp__size-pill-label';
        pillLabel.textContent = c.value;
        pill.appendChild(pillLabel);
        if (state === 'sold-out') {
          pill.setAttribute('aria-disabled', 'true');
        } else {
          pill.addEventListener('click', function () {
            bundlePick(slot, c);
            bundleOmRender(mount, slot);
            // In the add-to-cart flow, the next product without a size
            // follows once the pick has shown.
            if (bundleChain) setTimeout(bundleOmAdvance, 250);
          });
        }
        pills.appendChild(pill);
      });
      box.appendChild(label);
      box.appendChild(pills);
    }

    // Add-to-cart flow: the next product without a size, or add the bundle.
    function bundleOmAdvance() {
      if (!bundleChain) return;
      var next = bundleNextUnpicked();
      if (next) {
        if (fillOmProduktet(next.chevron.getAttribute('data-product-handle'), next.chevron.getAttribute('data-variant-id'), addToCartBtn, next)) {
          omProduktetMount.scrollTop = 0;
          requestAnimationFrame(clampOmTeaser);
          // The pointer still rests where the last size was clicked: no hover
          // fill on the new product's sizes until it moves (it read as a
          // pick), and keyboard focus goes to its first size.
          var fresh = omProduktetMount.querySelector('[data-om-sizes]');
          if (fresh) {
            fresh.classList.add('is-fresh');
            omProduktetMount.addEventListener('pointermove', function () { fresh.classList.remove('is-fresh'); }, { once: true });
            var first = fresh.querySelector('.spejder-pdp__size-pill:not([aria-disabled="true"])');
            if (first) first.focus({ preventScroll: true });
          }
        }
        return;
      }
      bundleChain = false;
      closeOverlay(omProduktetHost);
      if (addToCartForm && typeof addToCartForm.requestSubmit === 'function') {
        addToCartForm.requestSubmit(addToCartBtn);
      } else if (addToCartBtn) {
        addToCartBtn.click();
      }
    }

    // Every product's row.
    bundleChoiceSlots.forEach(function (slot) {
      var row = root.querySelector('.spejder-pdp__bundle-row[data-bundle-slot="' + slot.key + '"]');
      if (!row) return;
      var nameEl = row.querySelector('.spejder-pdp__bundle-name');
      slot.row = row;
      slot.pickBtn = row.querySelector('[data-bundle-pick]');
      slot.pickLabel = slot.pickBtn ? slot.pickBtn.textContent.trim() : '';
      slot.chevron = row.querySelector('[data-bundle-drawer-trigger]');
      slot.title = nameEl ? nameEl.textContent.trim() : '';
    });

    if (bundleChoiceSlots.length && omProduktetHost) {
      // Closing the drawer without a pick ends the add-to-cart flow.
      var bundleOmDialog = omProduktetHost.querySelector('dialog');
      if (bundleOmDialog) bundleOmDialog.addEventListener('close', function () { bundleChain = false; });

      // Capture phase, like the size guard below: runs before product-form.js.
      if (addToCartBtn) {
        addToCartBtn.addEventListener(
          'click',
          function (e) {
            var next = bundleNextUnpicked();
            if (!next || !next.chevron) return;
            e.preventDefault();
            e.stopPropagation();
            bundleChain = true;
            openOmProduktet(next.chevron.getAttribute('data-product-handle'), next.chevron.getAttribute('data-variant-id'), addToCartBtn, next);
          },
          true
        );
      }
    }

    if (selectSizeHost) {
      bindDynamicPillTriggers();

      // ATC guard (human-verified correction, retry:3): on a size-optioned
      // product, clicking ATC with NO size explicitly chosen yet opens
      // Select-size as a prompt; with a size already chosen (inline pill or
      // a prior overlay pick) ATC proceeds straight to the normal add-to-
      // cart submit — the overlay never gates a resolved selection.
      // Capture-phase so it runs before product-form.js reads the submit
      // event.
      if (addToCartBtn && sizePillsEl) {
        addToCartBtn.addEventListener(
          'click',
          function (e) {
            if (sizeExplicitlyChosen) return; // already resolved — let it submit
            e.preventDefault();
            e.stopPropagation();
            openSelectSize(addToCartBtn);
          },
          true
        );
      }
    }
  }

  function init() {
    document.querySelectorAll('.spejder-pdp').forEach(initPDP);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
